import { useState, useEffect } from 'react';
import { FireIcon, SunIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon, ShieldCheckIcon, ClockIcon, ArrowRightIcon, CheckCircleIcon, PhoneIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/solid';
import HeatPumpIcon from './icons/HeatPumpIcon';
import MiniSplitIcon from './icons/MiniSplitIcon';
import veteranBadge from '../assets/optimized/veteranbadge.webp';
import acInstallationImg from '../assets/optimized/acinstallationrepair.webp';
import heatingServicesImg from '../assets/optimized/heatingservices.webp';
import heatPumpImg from '../assets/optimized/heatpumpsolutions.webp';
import miniSplitImg from '../assets/optimized/minisplitsystems.webp';
import preventiveMaintenanceImg from '../assets/optimized/thepreventivemaintenance.webp';
import emergencyHvacImg from '../assets/optimized/emergencyhvacservice.webp';
import { useContent } from '../context/ContentContext';
import type { ServiceItem } from '../types/cms';

const SERVICE_ICONS = [SunIcon, FireIcon, HeatPumpIcon, MiniSplitIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon];
const SERVICE_IMAGES = [acInstallationImg, heatingServicesImg, heatPumpImg, miniSplitImg, preventiveMaintenanceImg, emergencyHvacImg];

function MobileServiceAccordion({ services, bookingUrl }: { services: ServiceItem[]; bookingUrl: string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="lg:hidden space-y-3">
      {services.map((service, index) => {
        const Icon = SERVICE_ICONS[index] || WrenchScrewdriverIcon;
        const isOpen = openIndex === index;

        return (
          <div
            key={index}
            className={`rounded-xl border overflow-hidden transition-all duration-300 ${
              isOpen ? 'border-brand-orange/30 bg-white/[0.06]' : 'border-white/10 bg-white/[0.03]'
            }`}
          >
            <button
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="w-full flex items-center gap-3 p-4 text-left"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                isOpen ? 'bg-brand-orange' : 'bg-white/10'
              }`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <span className={`flex-1 font-semibold text-[15px] transition-colors duration-300 ${
                isOpen ? 'text-white' : 'text-white/70'
              }`}>
                {service.title}
              </span>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                isOpen ? 'bg-brand-orange/20' : 'bg-white/10'
              }`}>
                {isOpen ? (
                  <MinusIcon className="w-4 h-4 text-brand-orange" />
                ) : (
                  <PlusIcon className="w-4 h-4 text-white/50" />
                )}
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${
              isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="px-4 pb-5">
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>

                <div className="space-y-2 mb-5">
                  {service.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center gap-2">
                      <CheckCircleIcon className="w-4 h-4 text-brand-orange flex-shrink-0" />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <a
                  href={bookingUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-orange text-white px-5 py-3 rounded-lg font-semibold text-sm w-full justify-center"
                >
                  <span>Get a Free Estimate</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DesktopServiceShowcase({ services, bookingUrl, phone }: { services: ServiceItem[]; bookingUrl: string; phone: string }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#services')) {
        const params = new URLSearchParams(hash.split('?')[1] || '');
        const serviceParam = params.get('service');
        if (serviceParam !== null) {
          const index = parseInt(serviceParam, 10);
          if (index >= 0 && index < services.length) {
            setActiveIndex(index);
          }
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [services.length]);

  const activeService = services[activeIndex];
  const ActiveIcon = SERVICE_ICONS[activeIndex] || WrenchScrewdriverIcon;
  const activeImage = SERVICE_IMAGES[activeIndex] || acInstallationImg;

  return (
    <div className="hidden lg:grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-3 flex lg:flex-col gap-1">
        {services.map((service, index) => {
          const Icon = SERVICE_ICONS[index] || WrenchScrewdriverIcon;
          const isActive = activeIndex === index;

          return (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`group relative flex items-center gap-3 px-4 py-4 rounded-xl text-left transition-all duration-300 ${
                isActive
                  ? 'bg-white/10 border border-white/10'
                  : 'hover:bg-white/5 border border-transparent'
              }`}
            >
              {isActive && (
                <span className="absolute left-0 top-3 bottom-3 w-1 bg-brand-orange rounded-full" />
              )}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                isActive
                  ? 'bg-brand-orange shadow-lg shadow-brand-orange/25'
                  : 'bg-white/5 group-hover:bg-white/10'
              }`}>
                <Icon className={`w-5 h-5 transition-colors duration-300 ${
                  isActive ? 'text-white' : 'text-white/40 group-hover:text-white/70'
                }`} />
              </div>
              <div className="min-w-0">
                <p className={`font-semibold text-sm transition-colors duration-300 ${
                  isActive ? 'text-white' : 'text-white/50 group-hover:text-white/80'
                }`}>
                  {service.title}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <div className="lg:col-span-9">
        <div className="rounded-2xl overflow-hidden border border-white/10">
          <div className="grid lg:grid-cols-2">
            <div
              key={`content-${activeIndex}`}
              className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center bg-white/[0.03] animate-fade-in"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-brand-orange rounded-xl flex items-center justify-center shadow-lg shadow-brand-orange/25">
                  <ActiveIcon className="w-6 h-6 text-white" />
                </div>
                <span className="text-white/30 font-mono text-sm">
                  {String(activeIndex + 1).padStart(2, '0')} / {String(services.length).padStart(2, '0')}
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-4">
                {activeService.title}
              </h3>

              <p className="text-white/60 leading-relaxed mb-6">
                {activeService.description}
              </p>

              <div className="flex flex-col gap-2.5 mb-8">
                {activeService.features.map((feature, fIndex) => (
                  <div
                    key={fIndex}
                    className="flex items-center gap-2.5 animate-fade-in"
                    style={{ animationDelay: `${(fIndex + 1) * 80}ms`, animationFillMode: 'both' }}
                  >
                    <CheckCircleIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-white/80 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <a
                  href={bookingUrl} target="_blank" rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 bg-brand-orange text-white px-7 py-4 rounded-lg font-semibold text-base hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25"
                >
                  <span>Get a Free Estimate</span>
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href={`tel:${phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center gap-2 text-white/70 font-semibold text-base hover:text-brand-orange transition-colors duration-300"
                >
                  <PhoneIcon className="w-5 h-5" />
                  <span>{phone}</span>
                </a>
              </div>
            </div>

            <div className="relative min-h-[420px] xl:min-h-[560px]">
              <img decoding="async" loading="lazy"
                key={activeIndex}
                src={activeImage}
                width={800}
                height={1067}
                alt={activeService.title}
                className="absolute inset-0 w-full h-full object-cover animate-fade-in"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/20 to-transparent" />
              <img decoding="async" loading="lazy" width={200} height={208}
                src={veteranBadge}
                alt="Veteran Owned Business"
                className="absolute top-[30px] right-[30px] w-20 h-auto z-10"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  const { content, settings } = useContent();
  const svc = content.services;

  return (
    <section id="services" className="relative overflow-hidden" style={{ backgroundColor: '#111b21' }}>
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-24 xl:py-40">
        <div className="mb-8 sm:mb-16">
          <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-brand-orange rounded-full" />
            <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
              {svc.sectionLabel}
            </span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-off-white mb-3 sm:mb-4">
                {svc.heading}
              </h2>
              <p className="text-sm sm:text-lg text-white/50 leading-relaxed max-w-2xl">
                {svc.description}
              </p>
            </div>

            <div className="hidden sm:flex items-center gap-5 sm:gap-8 flex-shrink-0">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{content.hero.stats.jobsCompleted}</p>
                <p className="text-white/40 text-xs sm:text-sm mt-1">Jobs Completed</p>
              </div>
              <div className="w-px h-10 sm:h-12 bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-brand-orange">{content.hero.stats.satisfaction}</p>
                <p className="text-white/40 text-xs sm:text-sm mt-1">Satisfaction</p>
              </div>
              <div className="w-px h-10 sm:h-12 bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{content.hero.stats.googleRating}</p>
                <p className="text-white/40 text-xs sm:text-sm mt-1">Google Rating</p>
              </div>
            </div>
          </div>
        </div>

        <MobileServiceAccordion services={svc.services} bookingUrl={settings.bookingUrl} />
        <DesktopServiceShowcase services={svc.services} bookingUrl={settings.bookingUrl} phone={settings.phone} />

        <div className="mt-8 sm:mt-16">
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-5 sm:gap-8 p-5 sm:p-8 lg:p-12 items-center">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-3 sm:mb-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-brand-orange rounded-lg flex items-center justify-center">
                    <ShieldCheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <span className="text-white/60 font-semibold text-xs sm:text-sm uppercase tracking-wider">
                    {svc.guidanceLabel}
                  </span>
                </div>

                <h3 className="text-xl sm:text-3xl font-serif font-bold text-white mb-3 sm:mb-4">
                  {svc.guidanceHeading}
                </h3>

                <p className="text-white/50 text-sm sm:text-lg leading-relaxed mb-3 sm:mb-6">
                  {svc.guidanceDescription}
                </p>

                <div className="hidden sm:flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                  <div className="flex items-center gap-2 text-white/40">
                    <ClockIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-sm">Same-day appointments</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/40">
                    <ShieldCheckIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-sm">Licensed {settings.license}</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 sm:gap-4">
                <a
                  href={settings.bookingUrl} target="_blank" rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 bg-brand-orange text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25 text-sm sm:text-base"
                >
                  <span>{svc.estimateButtonLabel}</span>
                  <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold hover:border-brand-orange hover:text-brand-orange transition-all duration-300 text-sm sm:text-base"
                >
                  <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Call {settings.phone}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
